import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import cls from 'classnames';
import AnchorNavigation from '@/molecules/AnchorNavigation/AnchorNavigationScroll';

import { getContent, isWHEnv } from '@/utils/helper';
import { useIsKorean } from '@/utils/useLocale';
import {
  analytics,
  getPrintDetail,
  getVisitWebsite,
  getDirections,
} from '@/utils/analytics';

import Button from '@/atoms/Button/Button';
import ClipAnimation from '@/atoms/ClipAnimation/ClipAnimation';
import Icon from '@/atoms/Icon/Icon';
import Pill from '@/atoms/Pill/Pill';
import Text from '@/atoms/Text/Text';
import SocialShare from '@/molecules/SocialShare/SocialShare';
import { DynamicAsset } from '@/molecules/Media/index';
import BottomSheet from '@/organisms/BottomSheet/BottomSheet';

function HeaderButton({
  canStartAnimation,
  onClick,
  iconName,
  label,
  hasRightBorder,
  wrapperStyle = '',
  iconColor,
  textColor,
}) {
  return (
    <div className={`flex ${wrapperStyle}`}>
      <ClipAnimation startAnimation={canStartAnimation}>
        <Button style="aaco-header-button flex items-center" onClick={onClick}>
          <Icon
            className={`mr-2.5 dd-en:mr-3.5 dd-kr:mr-3.5 ${iconColor}`}
            height={18}
            name={iconName}
            width={18}
          />
          <Text tag="tag" style={textColor}>
            {label}
          </Text>
        </Button>
      </ClipAnimation>
      {hasRightBorder && (
        <div className="border-l-1 border-black ml-4 md:mx-4" />
      )}
    </div>
  );
}

function RecipeHeader({
  anchorNavigation,
  assets = [],
  entries = [],
  fields = {},
}) {
  const [open, setOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [canStartAnimation, setCanStartAnimation] = useState(false);

  const isKorean = useIsKorean();

  const openWindowPrint = (e) => {
    analytics({
      window,
      document,
      detail: getPrintDetail(e),
    });
    window?.print();
  };
  const openDirection = (e) => {
    analytics({
      window,
      document,
      detail: getDirections(e, {
        url: fields?.mapLink,
      }),
    });
    window?.open(fields?.mapLink, '_blank');
  };
  const openWebsite = (e) => {
    analytics({
      window,
      document,
      detail: getVisitWebsite(e, {
        url: fields?.websiteLink,
      }),
    });
    window?.open(fields?.websiteLink, '_blank');
  };
  const openShare = () => setOpen(true);
  const closeShare = () => setOpen(false);

  const {
    title,
    asset,
    featuredTags,
    short,
    servings,
    maxServings,
    servingsName,
    time,
    cloudinaryAsset,
    shortLine2,
    websiteLink,
    mapLink,
  } = fields;

  const isWH = isWHEnv();

  useEffect(() => {
    setCanStartAnimation(true);

    return () => setCanStartAnimation(false);
  }, []);

  useEffect(() => {
    if (window?.location) {
      setPageUrl(window.location.href);
    }
  }, []);

  // index will be used to positioned the buttons with margin
  const mapIdx = mapLink ? 1 : 0;
  const webIdx = websiteLink ? mapIdx + 1 : 0;
  const printIdx = webIdx + 1 ?? 0;
  const shareIdx = printIdx + 1;

  return (
    <div className="aaco-recipe-header-wrapper relative">
      <div className={`aaco-recipe-header__container container mx-auto flex flex-col dd-kr:mx-0 dd-en:mx-0 dd-kr:p-0 dd-en:p-0 pb-8 md:pb-14`}>
        {/* START: Print view recipe */}
        <div className="shown-print aaco-header flex flex-col">
          <div className="aaco-header__image-desktop w-full brightness-50">
            <DynamicAsset field={asset} assets={assets} entries={entries} />
          </div>
          <Text tag="h3 mt-5">{title?.replace(/-/gm, '-\u2060')}</Text>
          <Text style="mt-3.5" tag="tag">
            {`${servings ?? 1} ${maxServings ? `- ${maxServings} ` : ''}${
              servingsName ? servingsName : `Serving${servings > 0 ? 's' : ''}`
            }${time ? ` • ${time}` : ''} • ${short}`}
          </Text>
        </div>
        {/* END: Print view recipe */}
        <div
          className={cls(
            'hidden-print aaco-header aaco-recipe-header',
            'dd-kr:h-[calc(100vh-5rem)] dd-en:h-[calc(100vh-5rem)] pt-4',
            'flex column flex-col-reverse dd-kr:flex-col dd-en:flex-col justify-center items-center dd-kr:items-end dd-en:items-end',
            'dd-kr:mb-20 dd-en:mb-20 md:mb-20', {
              'pt-10': isWH
            }
          )}
          aria-label={title}
        >
          <div className="relative w-full h-full dd-kr:mt-0 dd-en:mt-0 mt-10 md:mt-4">
            <div className="aaco-header__image-desktop h-full brightness-50">
              <DynamicAsset
                field={cloudinaryAsset ?? asset}
                assets={assets}
                entries={entries}
              />
            </div>
            <div className="aaco-header__bg-shadow absolute top-0 right-0 bottom-0 left-0 z-10 h-full w-full" />
          </div>
          <div className={`aaco-header__content h-max dd-kr:absolute dd-en:absolute relative w-full dd-kr:md:w-1/2 dd-en:md-w-1/2 text-left align-center z-20 dd-kr:px-5 dd-en:px-5 dd-kr:mb-[62px] dd-en:mb-[62px]`}>
            <ClipAnimation startAnimation={canStartAnimation} fullWidth>
              <div className={"aaco-header__featured-tags flex flex-wrap justify-left dd-en:justify-center dd-kr:justify-center gap-4 pb-6 md:pbb-5 dd-en:mb-4 dd-kr:mb-4 dd-en:md:mb-5 dd-kr:md:mb-5 "}>
                {featuredTags?.length > 0 &&
                  featuredTags.map((t) => {
                    const tag = getContent(t?.sys?.id, entries)?.fields;

                    if (!tag?.slug || !tag?.name) {
                      return null;
                    }

                    return (
                      <div
                        className="justify-center cursor-default"
                        key={tag?.name}
                      >
                        <Pill
                          tag="body"
                          style="pill-action mb-[5px] md:m-[5px] md:ml-0"
                          invert
                        >
                          {tag?.name}
                        </Pill>
                      </div>
                    );
                  })}
              </div>
            </ClipAnimation>

            {title && (
              <ClipAnimation startAnimation={canStartAnimation} fullWidth>
                <Text tag="h3" style="text-black pb-[13px] dd-en:pb-0 dd-kr:pb-0">
                  {title?.replace(/-/gm, '-\u2060')}
                </Text>
                <hr className="border-black" />
              </ClipAnimation>
            )}
            <div className="aaco-header__subtitle dd-en:hidden dd-kr:hidden flex dd-kr:justify-center dd-end:justify-center justify-start dd-en:mt-24 dd-kr:mt-24 mt-4 md:mt-4">
              <ClipAnimation startAnimation={canStartAnimation}>
                <Text style="mt-3.5	dd-kr:text-white dd-en:text-white text-black" tag="body-large">
                  {(servings
                    ? `${servings ?? 1} ${
                        maxServings ? `- ${maxServings} ` : ''
                      }${
                        servingsName
                          ? servingsName
                          : `Serving${servings > 0 ? 's' : ''}`
                      }${time ? ` • ${time}` : ''} • `
                    : '') + `${short}`}
                </Text>
                {shortLine2 && (
                  <div>
                    <Text style="mt-3.5	text-black" tag="tag">
                      {shortLine2}
                    </Text>
                  </div>
                )}
              </ClipAnimation>
            </div>
            <div className={cls(
              "aaco-header__cta md:hidden md:container md:mx-auto grid grid-cols-2 justify-center mt-6 dd-en:mt-[30px] dd-kr:mt-[30px] gap-y-[26px]", {
                "!flex md:!hidden !flex-row !justify-start !flex-wrap": isWH
              }
            )}>
              {mapLink && (
                <HeaderButton
                  canStartAnimation={canStartAnimation}
                  onClick={openDirection}
                  iconName="map"
                  label="Directions"
                  wrapperStyle={isWH ? "justify-start" : "justify-end"}
                  hasRightBorder
                  iconColor="text-black"
                  textColor="text-black"
                />
              )}
              {websiteLink && (
                <HeaderButton
                  canStartAnimation={canStartAnimation}
                  onClick={openWebsite}
                  iconName="web"
                  label="Visit Website"
                  wrapperStyle={
                    webIdx % 2 === 0 ? 'justify-start ml-4' : isWH ? "justify-start" : "justify-end"
                  }
                  iconColor="text-black"
                  textColor="text-black"
                />
              )}
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openWindowPrint}
                iconName="print"
                label={isKorean ? '프린트' : 'Print'}
                wrapperStyle={
                  printIdx % 2 === 0 ? 'justify-start  ml-4' : isWH ? "justify-start" : "justify-end"
                }
                hasRightBorder
                iconColor="text-black"
                textColor="text-black"
              />
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openShare}
                iconName="share"
                label={isKorean ? '공유' : 'Share'}
                wrapperStyle={
                  shareIdx % 2 === 0 ? 'justify-start  ml-4' : isWH ? "justify-start" : "justify-end"
                }
                iconColor="text-black"
                textColor="text-black"
              />
            </div>
            <div className="hidden md:flex aaco-header__cta dd-kr:justify-center dd-en:justify-center justify-start py-6 dd-kr:md:mt-[30px] dd-en:md:mt-[30px]">
              {mapLink && (
                <HeaderButton
                  canStartAnimation={canStartAnimation}
                  onClick={openDirection}
                  iconName="map"
                  label="Directions"
                  hasRightBorder
                  iconColor="text-black"
                  textColor="text-black"
                />
              )}
              {websiteLink && (
                <HeaderButton
                  canStartAnimation={canStartAnimation}
                  onClick={openWebsite}
                  iconName="web"
                  label="Visit Website"
                  hasRightBorder
                  iconColor="text-black"
                  textColor="text-black"
                />
              )}
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openWindowPrint}
                iconName="print"
                label={isKorean ? '프린트' : 'Print'}
                iconColor="text-black"
                textColor="text-black"
                hasRightBorder
              />
              <HeaderButton
                canStartAnimation={canStartAnimation}
                onClick={openShare}
                iconName="share"
                label={isKorean ? '공유' : 'Share'}
                iconColor="text-black"
                textColor="text-black"
              />
            </div>
          </div>
          {/* START: share modal */}
          <div>
            <BottomSheet open={open} onDismiss={closeShare}>
              <SocialShare url={pageUrl} size={40} title={title} />
            </BottomSheet>
          </div>
          {/* END: share modal */}
        </div>

      </div>
      {/* START: anchor navigation */}
      {isWH && anchorNavigation && (
          <AnchorNavigation
            anchor={anchorNavigation}
            assets={assets}
            entries={entries}
          />
        )}
        {/* END: anchor navigation */}
    </div>
  );
}

RecipeHeader.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default RecipeHeader;
